import { type GetServerSidePropsResult } from 'next';

import SupportIcon from '@mui/icons-material/Support';

import { Divider, Stack } from '@craft/ui/components/structural';
import { Typography } from '@craft/ui/components/typography';

import { parseUserFromSession, publicPageContext } from '@/src/auth/server';
import { CraftLogo } from '@/src/components-v2/brand/CraftLogo';
import { Page } from '@/src/components-v2/layout/Page';
import { type AppPage, type GetPublicPageServerSideProps } from '@/src/components-v2/types';
import { Pages } from '@/src/config/pages';
import { SignInButton } from '@/src/features/auth/components/buttons/SignInButton';
import { SupportArticleLink } from '@/src/features/support/components/buttons/SupportArticleLink';

type WelcomePageProps = {
  readonly returnTo: null | string;
};

const WelcomePage: AppPage<WelcomePageProps> = ({ returnTo }) => (
  <Page alignItems='center' justifyContent='center'>
    <Stack alignItems='center' gap={3} maxWidth='min(100%, 320px)' width='320px'>
      <Stack alignItems='center' direction='row' paddingX={2}>
        <CraftLogo loading='eager' preload size={{ width: '100%' }} />
      </Stack>
      <SignInButton fullWidth returnTo={returnTo} />
      <Divider sx={{ width: '100%' }} />
      <Stack alignItems='center' gap={2} width='100%'>
        <Typography width='100%'>Need help?</Typography>
        <SupportArticleLink.Base
          aria-label='Craft Help Center'
          endIcon={null}
          fullWidth
          startIcon={SupportIcon}
          variant='outlined'
        >
          Help Center
        </SupportArticleLink.Base>
      </Stack>
    </Stack>
  </Page>
);

WelcomePage.isLayoutScrollable = false;

export const getServerSideProps = publicPageContext(
  { page: 'welcome' },
  async ({ query, req, res }): Promise<GetServerSidePropsResult<WelcomePageProps>> => {
    const { user } = await parseUserFromSession({ req, res }, { strict: false });
    const returnTo =
      typeof query.returnTo === 'string' && query.returnTo.startsWith('/') ? query.returnTo : null;
    if (user) {
      return {
        redirect: { destination: returnTo ?? Pages.constructPath('home', {}), permanent: false },
      };
    }
    return { props: { returnTo } };
  },
) satisfies GetPublicPageServerSideProps<WelcomePageProps>;

export default WelcomePage;
